import { createReducer, on } from "@ngrx/store";
import { Project } from "../shared/Models/project-model";
import { AppActions, AppApiActions } from "./app.actions";
import { BillingSummaryFilters, initialBillingSummaryFilters } from './app.state';


import { User } from "oidc-client";



export interface AppState {
  language: string;
  theme: string;
  isLoading: boolean;
  selectableProjects: Project[];
  /** True once a selectable-projects list has arrived, so an empty list can be told from "not loaded yet". */
  selectableProjectsLoaded?: boolean;
  currentProject: Project | null;
  currentProjectId: number|null;
  errorMessage: string;
  currentUser:User|null;
  billingSummaryFilters: BillingSummaryFilters;

}

const initialState: AppState = {
  language: 'fi',
  theme: 'light',
  isLoading: false,
  selectableProjects: [],
  currentProject: null,
  currentProjectId: null,
  errorMessage: '',
  currentUser:null,
  billingSummaryFilters: initialBillingSummaryFilters,
};


/** Order-insensitive signature of what the project pickers display, so a changed name or end date is applied. */
function projectListSignature(projects: Project[]): string {
  return projects
    .map(p => `${p.id}|${p.name}|${p.costCenterNumber}|${p.endDate}`)
    .sort()
    .join(';');
}

export const appReducer = createReducer<AppState>(
  initialState,
  on(AppActions.setLanguage, (state, { language }) => {
    return {
      ...state,
      language,
    };
  }),
  on(AppActions.setTheme, (state, { theme }):AppState => {
    return {
      ...state,
      theme,
    };
  }),
  on(AppActions.setIsLoading, (state, { isLoading }):AppState => {
    return {
      ...state,
      isLoading,
    };
  }),
  on(AppActions.loadSelectableProjects, (state):AppState => {
    // Keep the existing list visible while refreshing — avoids a null flash
    // in all project-dependent components on every token renewal.
    return {
      ...state,
      isLoading: true,
    };
  }),
  on(AppApiActions.loadSelectableProjectsSuccess, (state, { selectableProjects }):AppState => {
    // A current project the user can no longer pick (ended, removed from the project) is cleared
    // so the selector shows "select project" and features stop using it.
    const currentId = state.currentProjectId;
    const stale = currentId != null && !selectableProjects.some(p => p.id === currentId);
    // Keep the existing list (and its reference) when nothing the pickers display has changed,
    // to avoid needless re-emits on every token renewal.
    const unchanged = !!state.selectableProjectsLoaded
      && projectListSignature(selectableProjects) === projectListSignature(state.selectableProjects);
    if (unchanged && !stale) {
      return { ...state, isLoading: false };
    }
    const projects = unchanged ? state.selectableProjects : selectableProjects;
    const found = currentId != null ? projects.find(p => p.id === currentId) : undefined;
    return {
      ...state,
      selectableProjects: projects,
      selectableProjectsLoaded: true,
      isLoading: false,
      errorMessage: '',
      currentProjectId: stale ? null : state.currentProjectId,
      // Resolve (or refresh) the full object from the list; keep the settings already loaded for it.
      currentProject: found
        ? { ...found, settings: state.currentProject?.id === found.id ? state.currentProject.settings ?? found.settings : found.settings }
        : null,
    };
  }),
  on(AppApiActions.loadSelectableProjectsFailure, (state, { errorMessage }):AppState => {
    return {
      ...state,
      errorMessage,
      isLoading: false,
      selectableProjects: [],
      selectableProjectsLoaded: false,
    };
  }),
  on(AppActions.setCurrentProject, (state, { currentProjectId }): AppState => {
    // No-op if the project is identical and already fully resolved.
    if (state.currentProjectId === currentProjectId && state.currentProject != null) {
      return state;
    }
    // The list has loaded and does not contain this project: the user cannot pick it, so do not adopt it.
    if (state.selectableProjectsLoaded && !state.selectableProjects.some(p => p.id === currentProjectId)) {
      return { ...state, currentProjectId: null, currentProject: null };
    }
    return {
      ...state,
      currentProjectId: currentProjectId,
      // Resolve the full object immediately if the list is available.
      // If the list hasn't loaded yet, currentProject stays null and will be
      // filled in by the loadSelectableProjectsSuccess handler above.
      currentProject: state.selectableProjects.find(p => p.id === currentProjectId) ?? null,
    };
  }),
  on(AppActions.setCurrentUser, (state , { currentUser }): AppState => {
    return {
      ...state,
      currentUser: currentUser,
    };
  }),
  on(AppActions.clearCurrentUser, (state): AppState => {
    return {
      ...state,
      currentUser: null,
      selectableProjects: [],
      selectableProjectsLoaded: false,
      currentProject: null,
      currentProjectId: null,
    };
  }),
  on(AppActions.setBillingSummaryFilters, (state, { filters }): AppState => {
    return {
      ...state,
      billingSummaryFilters: {
        ...state.billingSummaryFilters,
        ...filters
      }
    };
  }),
  on(AppApiActions.loadProjectSettingsSuccess, (state, { settings }): AppState => ({
    ...state,
    currentProject: state.currentProject ? { ...state.currentProject, settings } : null,
  })),
);

